import { z } from 'zod/v4';

import { partialHoverEffectsSchema, partialMenuItemSchema } from '../menu-shared/menu-item.schema';

/**
 * THE navigation widget data shape as AI tools accept it — flat, all-optional, shared by
 * `create_navigation_widget`/`update_navigation_widget`. Field names match the entity 1:1 (no
 * renaming fold needed except `hoverEffects.type`'s numeric-index mapping — see
 * `menu-shared/menu-item.schema.ts`).
 *
 * `menuList`, when provided, REPLACES the widget's list wholesale — there is no per-item merge
 * with what was there before (the model never sees `id`, so there is no stable key to merge by).
 * Omit it to leave the existing list untouched on update, or accept the 3-item default on create.
 */
export const partialNavigationWidgetDataSchema = z.object({
    menuList: z
        .array(partialMenuItemSchema)
        .optional()
        .describe('The navigation entries, left to right (or top to bottom for layoutDirection "Vertical").'),
    textColor: z.string().optional().describe('Menu item text color.'),
    submenuTextColor: z.string().optional().describe('Submenu (dropdown) item text color.'),
    fontSize: z.number().int().positive().optional().describe('Menu item font size in pixels.'),
    submenuFontSize: z.number().int().positive().optional().describe('Submenu item font size in pixels.'),
    menuItemIndent: z.enum(['S', 'M', 'L']).optional().describe('Spacing between menu items.'),
    horizontalPosition: z
        .enum(['L', 'M', 'R'])
        .optional()
        .describe('Where the whole menu sits in its column: left, center, or right.'),
    fontStyle: z
        .array(z.enum(['I', 'B', 'AT']))
        .optional()
        .describe('Menu item text style flags: "I" italic, "B" bold, "AT" all-caps. Empty array for plain text.'),
    submenuFontStyle: z
        .array(z.enum(['I', 'B', 'AT']))
        .optional()
        .describe('Same style flags for submenu items.'),
    submenuListBackground: z.string().optional().describe('Background color of an open dropdown.'),
    layoutDirection: z.enum(['Horizontal', 'Vertical']).optional().describe('Menu items in a row or a column.'),
    hoverEffects: partialHoverEffectsSchema
        .optional()
        .describe('What happens to a menu item on hover. Partial — only the keys you provide change.'),
});

export type PartialNavigationWidgetData = z.infer<typeof partialNavigationWidgetDataSchema>;
